import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, Link2 } from 'lucide-react';
import { toast } from 'sonner';

function buildUrl(baseUrl, params) {
  if (!baseUrl.trim()) return '';
  let url;
  try {
    url = new URL(baseUrl.trim());
  } catch {
    return '';
  }
  for (const [key, value] of Object.entries(params)) {
    if (value && value.trim()) url.searchParams.set(key, value.trim());
  }
  return url.toString();
}

/**
 * Constructor de UTM — conserva query params existentes de la URL base
 * (nunca los borra), previsualiza la URL final, y permite copiar o
 * insertar directamente en el copy. Sin acortador falso: si algún día se
 * conecta un servicio real de acortado, se agrega aquí, no antes.
 */
export default function UtmBuilder({ open, onClose, onInsert, network = 'facebook' }) {
  const [baseUrl, setBaseUrl] = useState('');
  const [source, setSource] = useState(network);
  const [medium, setMedium] = useState('social');
  const [campaign, setCampaign] = useState('');
  const [term, setTerm] = useState('');
  const [content, setContent] = useState('');

  useEffect(() => { if (open) setSource(network); }, [open, network]);

  const finalUrl = useMemo(() => buildUrl(baseUrl, {
    utm_source: source, utm_medium: medium, utm_campaign: campaign, utm_term: term, utm_content: content,
  }), [baseUrl, source, medium, campaign, term, content]);

  const isValid = baseUrl.trim() && finalUrl;

  const copy = () => {
    navigator.clipboard.writeText(finalUrl);
    toast.success('URL copiada');
  };

  const insert = () => {
    onInsert?.(finalUrl);
    onClose?.();
  };

  const clear = () => {
    setBaseUrl(''); setCampaign(''); setTerm(''); setContent('');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Link2 className="w-4 h-4" /> Constructor de UTM</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">URL base</Label>
            <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://tunegocio.com/promo" data-testid="utm-base-url" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">utm_source</Label><Input value={source} onChange={(e) => setSource(e.target.value)} /></div>
            <div><Label className="text-xs">utm_medium</Label><Input value={medium} onChange={(e) => setMedium(e.target.value)} /></div>
            <div><Label className="text-xs">utm_campaign</Label><Input value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="lanzamiento_julio" /></div>
            <div><Label className="text-xs">utm_term</Label><Input value={term} onChange={(e) => setTerm(e.target.value)} /></div>
          </div>
          <div><Label className="text-xs">utm_content</Label><Input value={content} onChange={(e) => setContent(e.target.value)} placeholder="video_reel" /></div>
          {finalUrl && (
            <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2">
              <p className="text-[10px] text-[#94A3B8] mb-1">URL final</p>
              <p className="text-xs text-[#0F172A] break-all font-mono">{finalUrl}</p>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" size="sm" onClick={clear}>Limpiar</Button>
          <Button variant="outline" size="sm" onClick={copy} disabled={!isValid} className="gap-1"><Copy className="w-3.5 h-3.5" /> Copiar</Button>
          <Button size="sm" onClick={insert} disabled={!isValid} data-testid="utm-insert-button">Insertar en el copy</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
